import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiRefreshCw, FiArrowUpRight, FiUser } from 'react-icons/fi';
import { usePropsStore } from '../stores/propsStore';
import { useParlayStore } from '../stores/parlayStore';
import { Sport } from '../domain/types';
import { PlayerPhoto } from '../components/PlayerPhoto';
import { PropDetail } from './SportScreen';

type News = { id: string; headline: string; description?: string; link: string; image?: string; sport: Sport };

const ESPN = 'https://site.api.espn.com/apis/site/v2/sports';

export function HomeScreen() {
  const navigate = useNavigate();
  const { allProps, isLoading, lastFetched } = usePropsStore();
  const { addLeg } = useParlayStore();
  const [news, setNews] = useState<News[]>([]);
  const [newsLoading, setNewsLoading] = useState(true);
  const [schedule, setSchedule] = useState<Record<Sport, number>>({ NBA: 0, NHL: 0 });
  const [scheduleLoading, setScheduleLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | Sport>('all');
  const [activePick, setActivePick] = useState<(typeof allProps)[number] | null>(null);

  const loadNews = async () => {
    setNewsLoading(true);
    const feeds = await Promise.all([Sport.NBA, Sport.NHL].map(async sport => {
      const path = sport === Sport.NBA ? 'basketball/nba' : 'hockey/nhl';
      try {
        const response = await fetch(`${ESPN}/${path}/news?limit=12&region=br&lang=pt`);
        if (!response.ok) return [];
        const body = await response.json();
        return (body.articles ?? []).flatMap((article: any) => {
          const link = article.links?.web?.href;
          if (!article.headline || !link) return [];
          return [{ id: `${sport}-${article.id ?? link}`, headline: article.headline, description: article.description, link, image: article.images?.[0]?.url, sport }];
        });
      } catch { return []; }
    }));
    setNews(feeds.flat().slice(0, 12));
    setNewsLoading(false);
  };

  useEffect(() => {
    void loadNews();
    const date = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Sao_Paulo', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date()).replace(/-/g, '');
    Promise.all([Sport.NBA, Sport.NHL].map(async sport => {
      const path = sport === Sport.NBA ? 'basketball/nba' : 'hockey/nhl';
      try {
        const response = await fetch(`${ESPN}/${path}/scoreboard?limit=100&dates=${date}`);
        const body = response.ok ? await response.json() : {};
        return [sport, body.events?.length ?? 0] as const;
      } catch { return [sport, 0] as const; }
    })).then(entries => setSchedule(Object.fromEntries(entries) as Record<Sport, number>)).finally(() => setScheduleLoading(false));
  }, []);

  const topProps = useMemo(() => allProps
    .filter(p => filter === 'all' || p.sport === filter)
    .filter(p => p.odds >= 1.4 && p.odds <= 2.2 && (p.overSample ?? p.underSample ?? 0) >= 5 && (p.hitRatePct ?? 0) >= 60)
    .sort((a, b) => (b.hitRatePct ?? 0) - (a.hitRatePct ?? 0) || Math.max(b.overSample ?? 0,b.underSample ?? 0) - Math.max(a.overSample ?? 0,a.underSample ?? 0))
    .slice(0, 6), [allProps, filter]);
  const dailyParlays = useMemo(() => {
    const eligible = allProps.filter(p => (filter === 'all' || p.sport === filter) && (p.hitRatePct ?? 0) >= 60 && p.odds >= 1.4 && p.odds <= 2.2);
    const byGame = Array.from(new Map(eligible.map(p => [p.game, eligible.filter(item => item.game === p.game).sort((a, b) => (b.hitRatePct ?? 0) - (a.hitRatePct ?? 0)).slice(0, 2)])).values());
    const combinations: (typeof allProps[number])[][] = [];
    for (let a = 0; a < byGame.length - 2 && combinations.length < 3; a++) {
      for (let b = a + 1; b < byGame.length - 1 && combinations.length < 3; b++) {
        for (let c = b + 1; c < byGame.length && combinations.length < 3; c++) combinations.push([byGame[a][0], byGame[b][0], byGame[c][0]]);
      }
    }
    return combinations.filter(parlay => parlay.every(Boolean));
  }, [allProps, filter]);

  return (
    <main className="screen home-screen">
      <div className="android-app-header"><img src="/rush-props-logo.png" alt="Rush Props" /><button aria-label="Abrir perfil" onClick={() => navigate('/profile')}><FiUser /></button></div>
      <header className="home-welcome">
        <p className="eyebrow">RUSH PROPS</p>
        <h1>BEM-VINDO(A)</h1>
        <p className="home-date">{new Intl.DateTimeFormat('pt-BR', { weekday: 'long', day: '2-digit', month: 'long' }).format(new Date()).toUpperCase()}</p>
        {lastFetched && <span className="home-updated">Atualizado às {new Date(lastFetched).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}</span>}
      </header>

      <section className="schedule-pair" aria-label="Jogos de hoje">
        {([Sport.NBA, Sport.NHL] as Sport[]).map(sport => <button key={sport} className={`schedule-tile ${sport.toLowerCase()}`} onClick={() => navigate(`/schedule/${sport.toLowerCase()}`)}>
          <span>{sport}</span><small>{scheduleLoading ? 'Buscando partidas…' : `${schedule[sport]} partidas hoje`}</small><FiArrowUpRight className="tile-arrow" />
        </button>)}
      </section>

      <section className="home-section">
        <div className="section-heading"><h2>NOTÍCIAS</h2><button className="text-action" onClick={() => void loadNews()} disabled={newsLoading}><FiRefreshCw className={newsLoading ? 'turning' : ''} /> ESPN · ATUALIZAR</button></div>
        {news.length ? <div className="news-rail">{news.map(item => <a className="news-card" key={item.id} href={item.link} target="_blank" rel="noreferrer">
          {item.image ? <img src={item.image} alt="" loading="lazy" /> : <div className="news-placeholder">{item.sport === Sport.NBA ? 'NBA' : 'NHL'}</div>}
          <span className={`sport-kicker ${item.sport.toLowerCase()}`}>{item.sport}</span><strong>{item.headline}</strong>
        </a>)}</div> : <div className="news-empty">{newsLoading ? 'Carregando notícias da NBA e NHL…' : 'Notícias indisponíveis no momento.'}</div>}
      </section>

      <section className="sport-filters" aria-label="Filtrar por liga">
        {([{ id: 'all', label: 'TODOS' }, { id: Sport.NBA, label: 'NBA' }, { id: Sport.NHL, label: 'NHL' }] as const).map(item => <button key={item.id} className={filter === item.id ? 'selected' : ''} onClick={() => setFilter(item.id)}>{item.label}</button>)}
      </section>

      {topProps[0] && <section className="featured-pick">
        <span className="eyebrow">ESCOLHA EM DESTAQUE · {topProps[0].sport}</span>
        <div className="featured-main"><div><h2>{topProps[0].player.toUpperCase()}</h2><p>{topProps[0].market} · {topProps[0].game}</p></div><strong>{topProps[0].hitRatePct}%<small>HIT RATE</small></strong></div>
        <div className="featured-line"><b>{topProps[0].side.toUpperCase()} {topProps[0].line}</b><span>ODD {topProps[0].odds.toFixed(2)}</span></div>
        <button onClick={() => setActivePick(topProps[0])}>VER ANÁLISE <FiArrowUpRight /></button>
      </section>}

      <section className="home-section"><div className="section-heading"><h2>MÚLTIPLAS DO DIA</h2><span>🔥</span></div>
        <div className="daily-parlay-strip">{dailyParlays.length ? dailyParlays.map((parlay, index) => <button key={parlay.map(pick => pick.id).join('-')} onClick={() => { parlay.forEach(addLeg); navigate('/parlay'); }} className="daily-parlay-card"><span>MÚLTIPLA {String(index + 1).padStart(2, '0')} · 3 JOGOS</span>{parlay.map(pick => <strong key={pick.id}>{pick.player.toUpperCase()}</strong>)}<small>{parlay.map(pick => `${pick.sport} ${pick.side.toUpperCase()} ${pick.line}`).join(' · ')}</small></button>) : <p>{isLoading ? 'Carregando combinações…' : 'Ainda não há picks suficientes para combinações do dia.'}</p>}</div>
      </section>

      <section className="home-section top-props-section"><div className="section-heading"><h2>TOP PROPS</h2><span>POR HIT RATE</span></div>
        <div className="top-props-list">{topProps.map((pick, index) => {
          return <button className="top-prop-row" key={pick.id} onClick={() => setActivePick(pick)}>
            <span className="top-prop-rank">{String(index + 1).padStart(2, '0')}</span><PlayerPhoto pick={pick} className="home-player-photo" />
            <span className="top-prop-info"><b>{pick.player.toUpperCase()}</b><small>{pick.market} · {pick.game}</small><small>{pick.side.toUpperCase()} {pick.line} · @{pick.odds.toFixed(2)}</small></span>
            <span className="top-prop-hit">{pick.hitRatePct}%<small>HR</small></span>
          </button>;
        })}{topProps.length === 0 && <p className="news-empty">{isLoading ? 'Carregando props…' : 'Nenhuma prop com histórico disponível.'}</p>}</div>
      </section>
      {activePick && <PropDetail pick={activePick} onClose={() => setActivePick(null)} />}
      <div className="home-bottom-space" />
    </main>
  );
}
